import {
  effectiveAlaskaHawaiiDeparturePassengers,
  effectiveDomesticSegmentPassengers,
  effectiveInternationalFacilityPassengers,
  legAirExcise,
} from "./calculator";
import { money } from "./money";
import type { FlightLeg, Rates } from "./types";

/**
 * Assumed passenger-air rates used on historical internal Form 720 worksheets
 * (email / ForeFlight counter shorthand: $4.80 domestic, $21.10 intl; AK/HI from Comparison defaults).
 */
export const ASSUMED_LEGACY_FILED_PASSENGER_RATES = {
  domesticSegmentUsdPerPassengerPerSegment: 4.8,
  internationalFacilityUsdPerPerson: 21.1,
  alaskaHawaiiDomesticDepartureUsdPerPerson: 11.7,
} as const;

/**
 * IRS indexed passenger air amounts by **calendar year of the flight** (segment begins/ends).
 * Sources: Rev. Procs. cited below (Form 720 / §4261).
 */
export const IRS_INDEXED_PASSENGER_AIR_BY_YEAR: Record<
  number,
  { domestic: number; intl: number; akhi: number; cite: string }
> = {
  2023: {
    domestic: 4.8,
    intl: 21.1,
    akhi: 10.6,
    cite: "Rev. Proc. 2022-38 (CY 2023)",
  },
  2024: {
    domestic: 5.0,
    intl: 22.2,
    akhi: 11.1,
    cite: "Rev. Proc. 2023-34 (CY 2024)",
  },
  2025: {
    domestic: 5.2,
    intl: 22.9,
    akhi: 11.4,
    cite: "Rev. Proc. 2024-40 (CY 2025)",
  },
  2026: {
    domestic: 5.3,
    intl: 23.4,
    akhi: 11.7,
    cite: "Rev. Proc. 2025-32 (CY 2026)",
  },
};

export type F720RetrospectiveMetrics = {
  legCount: number;
  legacyAirExcise: number;
  officialAirExcise: number;
  deltaAirExcise: number;
  citation: string;
  legacyDomesticSegment: number;
  officialDomesticSegment: number;
  deltaDomesticSegment: number;
  legacyIntlFacility: number;
  officialIntlFacility: number;
  deltaIntlFacility: number;
  legacyAkHiDeparture: number;
  officialAkHiDeparture: number;
  deltaAkHiDeparture: number;
};

export type F720RetrospectiveYearRow = F720RetrospectiveMetrics & {
  year: number;
};

export type F720RetrospectiveQuarterRow = F720RetrospectiveMetrics & {
  year: number;
  quarter: 1 | 2 | 3 | 4;
  /** e.g. <code>2025 Q4</code> */
  periodLabel: string;
};

function calendarYear(isoDate: string): number | null {
  const y = parseInt(isoDate.slice(0, 4), 10);
  return Number.isFinite(y) ? y : null;
}

/** Calendar year + quarter (1–4) from <code>YYYY-MM-DD</code>, or null if invalid. */
export function calendarYearAndQuarter(
  isoDate: string
): { year: number; quarter: 1 | 2 | 3 | 4 } | null {
  const year = calendarYear(isoDate);
  if (year === null) return null;
  const m = parseInt(isoDate.slice(5, 7), 10);
  if (!Number.isFinite(m) || m < 1 || m > 12) return null;
  const quarter = Math.ceil(m / 3) as 1 | 2 | 3 | 4;
  return { year, quarter };
}

function withPassengerRates(
  base: Rates,
  domestic: number,
  intl: number,
  akhi: number
): Rates {
  return {
    ...base,
    domesticSegmentUsdPerPassengerPerSegment: domestic,
    internationalFacilityUsdPerPerson: intl,
    alaskaHawaiiDomesticDepartureUsdPerPerson: akhi,
  };
}

function sumDomesticSegmentTax(legs: FlightLeg[], domRate: number): number {
  let n = 0;
  for (const leg of legs) {
    if (leg.excludeFrom720) continue;
    const pax = effectiveDomesticSegmentPassengers(leg);
    if (pax <= 0 || leg.ruralAirportExempt) continue;
    const segs = Math.max(1, leg.segmentsDomestic);
    n = money(n + segs * pax * domRate);
  }
  return n;
}

function sumIntlFacilityTax(legs: FlightLeg[], intlRate: number): number {
  let n = 0;
  for (const leg of legs) {
    const ip = effectiveInternationalFacilityPassengers(leg);
    if (ip > 0) n = money(n + ip * intlRate);
  }
  return n;
}

function sumAkHiDepartureTax(legs: FlightLeg[], akRate: number): number {
  let n = 0;
  for (const leg of legs) {
    const ak = effectiveAlaskaHawaiiDeparturePassengers(leg);
    if (ak > 0) n = money(n + ak * akRate);
  }
  return n;
}

function sumLegAirExcise(legs: FlightLeg[], r: Rates): number {
  let n = 0;
  for (const leg of legs) {
    n = money(n + legAirExcise(leg, r));
  }
  return n;
}

/**
 * Passenger-air retrospective for legs in a single **calendar year** (must have an IRS indexed row).
 */
export function retrospectiveMetricsForLegsInYear(
  yLegs: FlightLeg[],
  year: number,
  baseRates: Rates
): F720RetrospectiveMetrics | null {
  const idx = IRS_INDEXED_PASSENGER_AIR_BY_YEAR[year];
  if (!idx || yLegs.length === 0) return null;

  const L = ASSUMED_LEGACY_FILED_PASSENGER_RATES;
  const legacyRates = withPassengerRates(
    baseRates,
    L.domesticSegmentUsdPerPassengerPerSegment,
    L.internationalFacilityUsdPerPerson,
    L.alaskaHawaiiDomesticDepartureUsdPerPerson
  );
  const officialRates = withPassengerRates(baseRates, idx.domestic, idx.intl, idx.akhi);

  const legacyDomesticSegment = sumDomesticSegmentTax(
    yLegs,
    L.domesticSegmentUsdPerPassengerPerSegment
  );
  const officialDomesticSegment = sumDomesticSegmentTax(yLegs, idx.domestic);
  const legacyIntlFacility = sumIntlFacilityTax(
    yLegs,
    L.internationalFacilityUsdPerPerson
  );
  const officialIntlFacility = sumIntlFacilityTax(yLegs, idx.intl);
  const legacyAkHiDeparture = sumAkHiDepartureTax(
    yLegs,
    L.alaskaHawaiiDomesticDepartureUsdPerPerson
  );
  const officialAkHiDeparture = sumAkHiDepartureTax(yLegs, idx.akhi);

  const legacyAirExcise = sumLegAirExcise(yLegs, legacyRates);
  const officialAirExcise = sumLegAirExcise(yLegs, officialRates);

  return {
    legCount: yLegs.length,
    legacyAirExcise,
    officialAirExcise,
    deltaAirExcise: money(officialAirExcise - legacyAirExcise),
    citation: idx.cite,
    legacyDomesticSegment,
    officialDomesticSegment,
    deltaDomesticSegment: money(officialDomesticSegment - legacyDomesticSegment),
    legacyIntlFacility,
    officialIntlFacility,
    deltaIntlFacility: money(officialIntlFacility - legacyIntlFacility),
    legacyAkHiDeparture,
    officialAkHiDeparture,
    deltaAkHiDeparture: money(officialAkHiDeparture - legacyAkHiDeparture),
  };
}

/**
 * Group legs by calendar year (from <code>flightDate</code>), compare passenger-air math at
 * assumed legacy worksheet rates vs IRS indexed rates for that year.
 */
export function computeF720RetrospectiveByYear(
  legs: FlightLeg[],
  baseRates: Rates
): {
  rows: F720RetrospectiveYearRow[];
  unknownYearLegCount: number;
} {
  const byYear = new Map<number, FlightLeg[]>();
  let unknownYearLegCount = 0;
  for (const leg of legs) {
    const y = calendarYear(leg.flightDate);
    if (y === null) {
      unknownYearLegCount++;
      continue;
    }
    let g = byYear.get(y);
    if (!g) {
      g = [];
      byYear.set(y, g);
    }
    g.push(leg);
  }

  const years = [...byYear.keys()].sort((a, b) => a - b);
  const rows: F720RetrospectiveYearRow[] = [];

  for (const year of years) {
    const yLegs = byYear.get(year)!;
    const m = retrospectiveMetricsForLegsInYear(yLegs, year, baseRates);
    if (!m) {
      unknownYearLegCount += yLegs.length;
      continue;
    }
    rows.push({ year, ...m });
  }

  return { rows, unknownYearLegCount };
}

/**
 * Group legs by **calendar quarter**, using IRS indexed passenger amounts for the leg’s **calendar year**.
 */
export function computeF720RetrospectiveByQuarter(
  legs: FlightLeg[],
  baseRates: Rates
): {
  rows: F720RetrospectiveQuarterRow[];
  unknownYearLegCount: number;
} {
  type Bucket = { year: number; quarter: 1 | 2 | 3 | 4; legs: FlightLeg[] };
  const map = new Map<string, Bucket>();
  let unknownYearLegCount = 0;

  for (const leg of legs) {
    const yq = calendarYearAndQuarter(leg.flightDate);
    if (!yq) {
      unknownYearLegCount++;
      continue;
    }
    if (!IRS_INDEXED_PASSENGER_AIR_BY_YEAR[yq.year]) {
      unknownYearLegCount++;
      continue;
    }
    const key = `${yq.year}-Q${yq.quarter}`;
    let b = map.get(key);
    if (!b) {
      b = { year: yq.year, quarter: yq.quarter, legs: [] };
      map.set(key, b);
    }
    b.legs.push(leg);
  }

  const buckets = [...map.values()].sort((a, b) =>
    a.year !== b.year ? a.year - b.year : a.quarter - b.quarter
  );

  const rows: F720RetrospectiveQuarterRow[] = [];
  for (const b of buckets) {
    const m = retrospectiveMetricsForLegsInYear(b.legs, b.year, baseRates);
    if (!m) continue;
    rows.push({
      ...m,
      year: b.year,
      quarter: b.quarter,
      periodLabel: `${b.year} Q${b.quarter}`,
    });
  }

  return { rows, unknownYearLegCount };
}
